import {
  CalendarIcon,
  DollarSignIcon,
  HomeIcon,
  UserIcon,
  UsersIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import client from "./api/client";
import { HomePage } from "./pages/HomePage";
import { LoginPage } from "./pages/LoginPage";
import { PayPage } from "./pages/PayPage";
import { ProfilePage } from "./pages/ProfilePage";
import { SchedulePage } from "./pages/SchedulePage";
import { TimeOffPage } from "./pages/TimeOffPage";
import "./index.css";
import type { EmployeeInfo } from "./api/QuikTripClient";
import Logo from "./logo.svg";

type Page = "schedule" | "timeoff" | "home" | "pay" | "profile";

const PAGE_TITLES: Record<Page, string> = {
  schedule: "Schedule",
  timeoff: "Time Off",
  home: "Home",
  pay: "Pay",
  profile: "Profile",
};

export function App() {
  const [loggedIn, setLoggedIn] = useState(() => client.isLoggedIn());
  const [activePage, setActivePage] = useState<Page>("home");
  const [employeeInfo, setEmployeeInfo] = useState<EmployeeInfo | null>(null);
  useEffect(() => {
    if (!loggedIn) return;
    client
      .getEmployeeInfo()
      .then(setEmployeeInfo)
      .catch(() => {
        client.logout();
        setLoggedIn(false);
      });
  }, [loggedIn]);

  if (!loggedIn) {
    return <LoginPage onLogin={() => setLoggedIn(true)} />;
  }

  return (
    <div className="mx-auto flex h-dvh flex-col overscroll-y-none">
      <div
        className="navbar bg-primary/75 text-primary-content flex-shrink-0 shadow-lg"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="flex flex-1 items-center justify-center gap-2 px-2 text-lg font-bold tracking-wide">
          <img className="inline-block h-6" src={Logo} alt="Logo" />
          {PAGE_TITLES[activePage]}
        </div>
      </div>

      <main className="bg-base-100 flex-1 overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+6rem)]">
        {localStorage.getItem("demoMode") === "true" && (
          <div className="alert position-absolute alert-info rounded-t-none border-t-0 p-2">
            <div>
              <span className="font-bold">Demo Mode:</span> Data is randomly
              generated and does not reflect actual schedules or pay stubs.
            </div>
          </div>
        )}
        {activePage === "schedule" && <SchedulePage />}
        {activePage === "timeoff" && <TimeOffPage />}
        {activePage === "home" && <HomePage employeeInfo={employeeInfo} />}
        {activePage === "pay" && <PayPage />}
        {activePage === "profile" && employeeInfo === null && (
          <div className="flex h-full items-center justify-center">
            <span className="loading loading-spinner loading-md text-primary" />
          </div>
        )}
        {activePage === "profile" && employeeInfo !== null && (
          <ProfilePage
            employeeInfo={employeeInfo}
            onLogout={() => {
              setLoggedIn(false);
              setEmployeeInfo(null);
            }}
          />
        )}
      </main>

      <div
        className="dock bg-base-200 border-base-300 h-24 shrink-0 overflow-visible border-t"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <button
          className={activePage === "schedule" ? "dock-active" : ""}
          onClick={() => setActivePage("schedule")}
          type="button"
        >
          <CalendarIcon />
          <span className="dock-label">Schedule</span>
        </button>
        <button
          className={activePage === "timeoff" ? "dock-active" : ""}
          onClick={() => setActivePage("timeoff")}
          type="button"
        >
          <UsersIcon />
          <span className="dock-label">Time Off</span>
        </button>
        <button
          className="relative"
          onClick={() => setActivePage("home")}
          type="button"
        >
          <div
            className={`flex h-12 w-12 -translate-y-4 items-center justify-center rounded-full shadow-lg transition-all duration-150 ${
              activePage === "home"
                ? "bg-primary shadow-primary/40 scale-110"
                : "bg-base-300 text-base-content/60"
            } `}
          >
            <HomeIcon
              size={22}
              className={activePage === "home" ? "text-white" : ""}
            />
          </div>
          <span
            className={`dock-label -mt-3 ${activePage === "home" ? "text-primary font-semibold" : ""}`}
          >
            Home
          </span>
        </button>
        <button
          className={activePage === "pay" ? "dock-active" : ""}
          onClick={() => setActivePage("pay")}
          type="button"
        >
          <DollarSignIcon />
          <span className="dock-label">Pay</span>
        </button>
        <button
          className={activePage === "profile" ? "dock-active" : ""}
          onClick={() => setActivePage("profile")}
          type="button"
        >
          <UserIcon />
          <span className="dock-label">Profile</span>
        </button>
      </div>
    </div>
  );
}

export default App;
